import { Box, Button, TextField, Typography, Container, Fade, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Lock } from "@mui/icons-material";
import { appColors } from "../../../constants/theme";
import {
    ForgotPasswordMainContainer,
    ForgotPasswordCard,
    ForgotPasswordHeader,
} from "../style";
import {
    useConfirmPasswordResetMutation,
    useConfirmEmployeePasswordResetMutation,
    useConfirmAssistantPasswordResetMutation,
} from "../../../store/api/usersApiSlice";
import {
    isPasswordResetTokenExpired,
    isPasswordResetTokenInvalid,
} from "../../../utils/passwordResetErrors";

const ResetPassword = ({ variant = "doctor" }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [confirmPasswordReset] = useConfirmPasswordResetMutation();
    const [confirmEmployeePasswordReset] = useConfirmEmployeePasswordResetMutation();
    const [confirmAssistantPasswordReset] = useConfirmAssistantPasswordResetMutation();
    const [submitting, setSubmitting] = useState(false);
    const [linkError, setLinkError] = useState(null);

    const token = searchParams.get("token");
    const emailFromQuery = searchParams.get("email") || "";

    const loginPath = useMemo(() => {
        if (variant === "employee") return "/superlogin";
        return "/login";
    }, [variant]);

    useEffect(() => {
        if (!token || !emailFromQuery) {
            setLinkError(t("password_reset_invalid_link"));
        } else {
            setLinkError(null);
        }
    }, [emailFromQuery, t, token]);

    const validationSchema = Yup.object().shape({
        newPassword: Yup.string()
            .min(8, t("password_reset_min_length"))
            .required(t("password_reset_min_length")),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref("newPassword")], t("password_reset_passwords_mismatch"))
            .required(t("password_reset_passwords_mismatch")),
    });

    const confirmReset = (body) => {
        if (variant === "assistant") return confirmAssistantPasswordReset(body).unwrap();
        if (variant === "employee") return confirmEmployeePasswordReset(body).unwrap();
        return confirmPasswordReset(body).unwrap();
    };

    const formik = useFormik({
        initialValues: { newPassword: "", confirmPassword: "" },
        validationSchema,
        onSubmit: async ({ newPassword, confirmPassword }) => {
            try {
                setSubmitting(true);
                await confirmReset({
                    email: emailFromQuery,
                    token,
                    newPassword,
                    confirmPassword,
                });
                toast.success(t("password_reset_success"));
                navigate(loginPath, { replace: true });
            } catch (err) {
                if (isPasswordResetTokenExpired(err)) {
                    toast.error(t("password_reset_expired_link"));
                } else if (isPasswordResetTokenInvalid(err)) {
                    toast.error(t("password_reset_invalid_link"));
                } else {
                    toast.error(t("something_went_wrong"));
                }
            } finally {
                setSubmitting(false);
            }
        },
    });

    const inputFieldStyle = {
        "& .MuiOutlinedInput-root": {
            borderRadius: "12px",
            backgroundColor: appColors.white,
        },
    };

    return (
        <ForgotPasswordMainContainer>
            <Container maxWidth="sm">
                <Fade in timeout={800}>
                    <ForgotPasswordCard>
                        <ForgotPasswordHeader>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 700,
                                    color: theme.palette.primary.main,
                                    textAlign: "center",
                                    flex: 1,
                                }}
                            >
                                {t("reset_password")}
                            </Typography>
                        </ForgotPasswordHeader>

                        {linkError ? (
                            <Box>
                                <Typography sx={{ color: theme.palette.text.secondary, mb: 3, textAlign: "center" }}>
                                    {linkError}
                                </Typography>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={() => navigate(loginPath)}
                                    sx={{ textTransform: "none", borderRadius: "12px", height: "48px" }}
                                >
                                    {t("forgot_password")}
                                </Button>
                            </Box>
                        ) : (
                            <Box component="form" onSubmit={formik.handleSubmit}>
                                {emailFromQuery && (
                                    <Typography sx={{ color: theme.palette.text.secondary, mb: 2, textAlign: "center" }}>
                                        {emailFromQuery}
                                    </Typography>
                                )}
                                <TextField
                                    fullWidth
                                    type="password"
                                    name="newPassword"
                                    label={t("password_reset_new_password")}
                                    value={formik.values.newPassword}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={Boolean(formik.touched.newPassword && formik.errors.newPassword)}
                                    helperText={formik.touched.newPassword && formik.errors.newPassword}
                                    sx={{ ...inputFieldStyle, mb: 2 }}
                                    InputProps={{
                                        startAdornment: <Lock sx={{ color: theme.palette.primary.main, mr: 1 }} />,
                                    }}
                                />
                                <TextField
                                    fullWidth
                                    type="password"
                                    name="confirmPassword"
                                    label={t("password_reset_confirm_password")}
                                    value={formik.values.confirmPassword}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={Boolean(formik.touched.confirmPassword && formik.errors.confirmPassword)}
                                    helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                                    sx={{ ...inputFieldStyle, mb: 3 }}
                                    InputProps={{
                                        startAdornment: <Lock sx={{ color: theme.palette.primary.main, mr: 1 }} />,
                                    }}
                                />
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    disabled={submitting}
                                    sx={{
                                        textTransform: "none",
                                        borderRadius: "12px",
                                        height: "48px",
                                        fontWeight: 600,
                                    }}
                                >
                                    {submitting ? t("sending") : t("password_reset_submit")}
                                </Button>
                            </Box>
                        )}
                    </ForgotPasswordCard>
                </Fade>
            </Container>
        </ForgotPasswordMainContainer>
    );
};

export default ResetPassword;
